import { useMemo } from "react"
import temples from "data/temples.json"
import airports from "data/airports.json"
import aaCodes from "data/airports-aa.json"
import zedCodes from "data/airports-zed.json"
import { haversineMi } from "../utils/distance"

// Build the airport lookup + per-network airport arrays once at module load.
// These never change during a session, so they live outside the hook.
const airportByIata = new Map(airports.map((a) => [a.iata, a]))
const aaAirports = aaCodes.map((c) => airportByIata.get(c)).filter(Boolean)
const zedAirports = zedCodes.map((c) => airportByIata.get(c)).filter(Boolean)

// Every address row is a candidate relative; the user controls inclusion via
// the RelativeFilterDialog (by category and by individual selection).
export function getRelatives(familyAddresses) {
  return familyAddresses || []
}

function closest(temple, airportList) {
  let best = null
  let bestMi = Infinity
  for (const ap of airportList) {
    const mi = haversineMi(temple.lat, temple.lon, ap.lat, ap.lon)
    if (mi < bestMi) {
      bestMi = mi
      best = ap
    }
  }
  return best ? { airport: best, mi: bestMi } : null
}

function closestRelative(temple, relatives) {
  let best = null
  let bestMi = Infinity
  for (const r of relatives) {
    if (typeof r.lat !== "number" || typeof r.lon !== "number") continue
    const mi = haversineMi(temple.lat, temple.lon, r.lat, r.lon)
    if (mi < bestMi) {
      bestMi = mi
      best = r
    }
  }
  return best ? { relative: best, mi: bestMi } : null
}

export function useTempleRows(familyAddresses, selectedRelativeIds) {
  return useMemo(() => {
    let relatives = getRelatives(familyAddresses)
    if (selectedRelativeIds instanceof Set) {
      relatives = relatives.filter((r) => selectedRelativeIds.has(r.id))
    }
    return temples.map((t) => {
      const aa = closest(t, aaAirports)
      const zed = closest(t, zedAirports)
      const rel = closestRelative(t, relatives)
      return {
        templeName: t.name,
        templeLocation: t.location,
        templeLat: t.lat,
        templeLon: t.lon,
        aaAirport: aa?.airport || null,
        aaMi: aa?.mi ?? null,
        zedAirport: zed?.airport || null,
        zedMi: zed?.mi ?? null,
        relative: rel?.relative || null,
        relativeMi: rel?.mi ?? null,
      }
    })
  }, [familyAddresses, selectedRelativeIds])
}

export const datasetStats = {
  temples: temples.length,
  aa: aaCodes.length,
  zed: zedCodes.length,
}
